import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InvestigationMetrics from './components/InvestigationMetrics';
import InvestigationFilterToolbar from './components/InvestigationFilterToolbar';
import InvestigationPanel from './components/InvestigationPanel';
import RecommendationTracker from './components/RecommendationTracker';
import InvestigationStatistics from './components/InvestigationStatistics';
import CrossLocationTracker from './components/CrossLocationTracker';

const IncidentInvestigationTracker = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();

  const [activeTab, setActiveTab] = useState('investigations');
  const [filters, setFilters] = useState({
    location: 'all',
    status: 'all',
    investigator: 'all',
    priority: 'all',
    search: '',
    startDate: '',
    endDate: ''
  });

  const mockInvestigations = [
    {
      id: 1,
      incidentId: 'INC-2025-001',
      title: 'Chemical spill investigation - Production Area 2',
      incidentType: 'injury',
      severity: 'critical',
      status: 'ongoing',
      investigator: 'Dr. Sarah Johnson',
      location: 'plant-1',
      incidentDate: '2025-01-10T14:30:00',
      initiatedDate: '2025-01-10T15:00:00',
      targetCompletionDate: '2025-01-20T17:00:00',
      progress: 65,
      recommendations: 3,
      implementedRecommendations: 1,
      documentsCollected: 12,
      interviewsCompleted: 8,
      rootCauseIdentified: true
    },
    {
      id: 2,
      incidentId: 'INC-2025-002',
      title: 'Near miss - Forklift collision averted',
      incidentType: 'near-miss',
      severity: 'moderate',
      status: 'completed',
      investigator: 'Michael Chen',
      location: 'warehouse-1',
      incidentDate: '2024-12-28T10:15:00',
      initiatedDate: '2024-12-28T11:00:00',
      targetCompletionDate: '2025-01-05T17:00:00',
      completionDate: '2025-01-04T16:30:00',
      progress: 100,
      recommendations: 5,
      implementedRecommendations: 5,
      documentsCollected: 8,
      interviewsCompleted: 5,
      rootCauseIdentified: true
    },
    {
      id: 3,
      incidentId: 'INC-2025-003',
      title: 'Minor injury - Slip and fall in cafeteria',
      incidentType: 'injury',
      severity: 'minor',
      status: 'ongoing',
      investigator: 'Emily Rodriguez',
      location: 'plant-2',
      incidentDate: '2025-01-12T12:45:00',
      initiatedDate: '2025-01-12T13:30:00',
      targetCompletionDate: '2025-01-18T17:00:00',
      progress: 40,
      recommendations: 2,
      implementedRecommendations: 0,
      documentsCollected: 5,
      interviewsCompleted: 3,
      rootCauseIdentified: false
    },
    {
      id: 4,
      incidentId: 'INC-2025-004',
      title: 'Equipment failure investigation - Hydraulic press',
      incidentType: 'near-miss',
      severity: 'major',
      status: 'overdue',
      investigator: 'Robert Thompson',
      location: 'plant-1',
      incidentDate: '2024-12-20T08:00:00',
      initiatedDate: '2024-12-20T09:00:00',
      targetCompletionDate: '2025-01-10T17:00:00',
      progress: 75,
      recommendations: 4,
      implementedRecommendations: 1,
      documentsCollected: 15,
      interviewsCompleted: 10,
      rootCauseIdentified: true
    },
    {
      id: 5,
      incidentId: 'INC-2025-005',
      title: 'Fire alarm system malfunction investigation',
      incidentType: 'near-miss',
      severity: 'critical',
      status: 'initiated',
      investigator: 'Lisa Anderson',
      location: 'plant-3',
      incidentDate: '2025-01-15T16:20:00',
      initiatedDate: '2025-01-15T17:00:00',
      targetCompletionDate: '2025-01-25T17:00:00',
      progress: 10,
      recommendations: 0,
      implementedRecommendations: 0,
      documentsCollected: 2,
      interviewsCompleted: 1,
      rootCauseIdentified: false
    }
  ];

  const mockRecommendations = [
    {
      id: 1,
      investigationId: 1,
      incidentId: 'INC-2025-001',
      recommendation: 'Install additional chemical containment barriers in Production Area 2',
      priority: 'high',
      responsibleParty: 'Facilities Manager',
      assignedTo: 'John Martinez',
      implementationDeadline: '2025-02-01T17:00:00',
      status: 'in-progress',
      implementationProgress: 45,
      estimatedCost: 15000,
      actualCost: null,
      category: 'engineering-control'
    },
    {
      id: 2,
      investigationId: 1,
      incidentId: 'INC-2025-001',
      recommendation: 'Conduct mandatory chemical handling refresher training for all operators',
      priority: 'high',
      responsibleParty: 'Training Manager',
      assignedTo: 'Sarah Wilson',
      implementationDeadline: '2025-01-25T17:00:00',
      status: 'completed',
      completionDate: '2025-01-18T14:00:00',
      implementationProgress: 100,
      estimatedCost: 5000,
      actualCost: 4800,
      category: 'training'
    },
    {
      id: 3,
      investigationId: 1,
      incidentId: 'INC-2025-001',
      recommendation: 'Update chemical spill response procedures and emergency protocols',
      priority: 'medium',
      responsibleParty: 'Safety Manager',
      assignedTo: 'Dr. Sarah Johnson',
      implementationDeadline: '2025-02-10T17:00:00',
      status: 'pending',
      implementationProgress: 0,
      estimatedCost: 2000,
      actualCost: null,
      category: 'administrative-control'
    },
    {
      id: 4,
      investigationId: 2,
      incidentId: 'INC-2025-002',
      recommendation: 'Install convex mirrors at warehouse blind corners',
      priority: 'medium',
      responsibleParty: 'Warehouse Manager',
      assignedTo: 'David Brown',
      implementationDeadline: '2025-01-20T17:00:00',
      status: 'completed',
      completionDate: '2025-01-15T10:00:00',
      implementationProgress: 100,
      estimatedCost: 3000,
      actualCost: 2850,
      category: 'engineering-control'
    },
    {
      id: 5,
      investigationId: 2,
      incidentId: 'INC-2025-002',
      recommendation: 'Implement designated pedestrian walkways in warehouse',
      priority: 'high',
      responsibleParty: 'Operations Manager',
      assignedTo: 'Michael Chen',
      implementationDeadline: '2025-01-30T17:00:00',
      status: 'completed',
      completionDate: '2025-01-22T16:00:00',
      implementationProgress: 100,
      estimatedCost: 8000,
      actualCost: 7500,
      category: 'engineering-control'
    },
    {
      id: 6,
      investigationId: 4,
      incidentId: 'INC-2025-004',
      recommendation: 'Upgrade hydraulic press preventive maintenance schedule',
      priority: 'critical',
      responsibleParty: 'Maintenance Manager',
      assignedTo: 'James Wilson',
      implementationDeadline: '2025-01-22T17:00:00',
      status: 'overdue',
      implementationProgress: 60,
      estimatedCost: 12000,
      actualCost: null,
      category: 'maintenance'
    }
  ];

  const filteredInvestigations = useMemo(() => {
    return mockInvestigations?.filter(investigation => {
      if (filters?.location !== 'all' && investigation?.location !== filters?.location) return false;
      if (filters?.status !== 'all' && investigation?.status !== filters?.status) return false;
      if (filters?.investigator !== 'all' && investigation?.investigator !== filters?.investigator) return false;
      if (filters?.priority !== 'all' && investigation?.severity !== filters?.priority) return false;
      
      if (filters?.search) {
        const searchLower = filters?.search?.toLowerCase();
        if (!investigation?.incidentId?.toLowerCase()?.includes(searchLower) && 
            !investigation?.title?.toLowerCase()?.includes(searchLower)) {
          return false;
        }
      }

      if (filters?.startDate) {
        const incidentDate = new Date(investigation?.incidentDate);
        const filterStart = new Date(filters?.startDate);
        if (incidentDate < filterStart) return false;
      }

      if (filters?.endDate) {
        const incidentDate = new Date(investigation?.incidentDate);
        const filterEnd = new Date(filters?.endDate);
        if (incidentDate > filterEnd) return false;
      }

      return true;
    });
  }, [filters, mockInvestigations]);

  const metrics = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    return {
      ongoingInvestigations: mockInvestigations?.filter(i => i?.status === 'ongoing')?.length,
      overdueInvestigations: mockInvestigations?.filter(i => i?.status === 'overdue')?.length,
      completedThisMonth: mockInvestigations?.filter(i => {
        if (!i?.completionDate) return false;
        return new Date(i?.completionDate) >= thirtyDaysAgo;
      })?.length,
      avgCompletionTime: 8.5,
      totalRecommendations: mockRecommendations?.length,
      implementedRecommendations: mockRecommendations?.filter(r => r?.status === 'completed')?.length,
      overdueRecommendations: mockRecommendations?.filter(r => r?.status === 'overdue')?.length,
      implementationRate: Math.round((mockRecommendations?.filter(r => r?.status === 'completed')?.length / mockRecommendations?.length) * 100)
    };
  }, [mockInvestigations, mockRecommendations]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      location: 'all',
      status: 'all',
      investigator: 'all',
      priority: 'all',
      search: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleViewDetails = (id) => {
    navigate(`/incident-investigation-workspace/${id}`);
  };

  return (
    <div className={`bg-background min-h-screen`}>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Incident Investigation Tracker</h1>
              <p className="text-muted-foreground mt-1">
                Track and manage ongoing incident investigations and recommendations
              </p>
            </div>
            <Button
              variant="outline"
              iconName="Home"
              onClick={() => navigate('/')}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>

        <InvestigationMetrics metrics={metrics} />

        <div className="mb-6">
          <div className="bg-card border border-border rounded-lg p-1 inline-flex gap-1">
            <button
              onClick={() => setActiveTab('investigations')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'investigations' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Search" size={16} className="inline mr-2" />
              Investigations
            </button>
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'recommendations' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="CheckCircle" size={16} className="inline mr-2" />
              Recommendations
            </button>
            <button
              onClick={() => setActiveTab('cross-location')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'cross-location' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Globe" size={16} className="inline mr-2" />
              Cross-Location
            </button>
            <button
              onClick={() => setActiveTab('statistics')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'statistics' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="BarChart3" size={16} className="inline mr-2" />
              Statistics
            </button>
          </div>
        </div>

        {activeTab === 'investigations' && (
          <>
            <InvestigationFilterToolbar
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              resultsCount={filteredInvestigations?.length}
            />

            <InvestigationPanel
              investigations={filteredInvestigations}
              onViewDetails={handleViewDetails}
            />
          </>
        )}

        {activeTab === 'recommendations' && (
          <RecommendationTracker
            recommendations={mockRecommendations}
            investigations={mockInvestigations}
          />
        )}

        {activeTab === 'cross-location' && (
          <CrossLocationTracker />
        )}

        {activeTab === 'statistics' && (
          <InvestigationStatistics
            investigations={mockInvestigations}
            recommendations={mockRecommendations}
          />
        )}
      </div>
    </div>
  );
};

export default IncidentInvestigationTracker;